import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Boxes } from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ImageUploader } from "./AdminProducts";

const EMPTY = { name: "", description: "", image_url: "", pricing_mode: "percent", fixed_price: "", percent_off: 10, is_active: true, components: [] };

export const AdminBundles = () => {
  const [bundles, setBundles] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/bundles").then(({ data }) => setBundles(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name, description: b.description || "", image_url: b.image_url || "",
      pricing_mode: b.pricing_mode, fixed_price: b.fixed_price || "", percent_off: b.percent_off || 10,
      is_active: !!b.is_active,
      components: b.components.map((c) => ({ ...c })),
    });
    setOpen(true);
  };

  const setComp = (i, patch) => setForm((f) => ({
    ...f, components: f.components.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
  }));
  const addComp = () => setForm((f) => ({ ...f, components: [...f.components, { product_id: "", quantity: 1 }] }));
  const removeComp = (i) => setForm((f) => ({ ...f, components: f.components.filter((_, idx) => idx !== i) }));

  const componentsTotal = form.components.reduce((sum, c) => {
    const p = products.find((x) => x.id === c.product_id);
    return sum + (p ? p.price * Math.max(1, +c.quantity || 1) : 0);
  }, 0);
  const previewPrice = form.pricing_mode === "percent"
    ? componentsTotal * (1 - (parseFloat(form.percent_off) || 0) / 100)
    : parseFloat(form.fixed_price) || 0;

  const save = async () => {
    const comps = form.components.filter((c) => c.product_id);
    if (!form.name.trim()) return toast.error("Bundle name is required");
    if (comps.length < 1) return toast.error("Pick at least 1 product for the bundle");
    setSaving(true);
    const payload = {
      name: form.name.trim(), description: form.description.trim(),
      image_url: form.image_url || null,
      components: comps.map((c) => ({ product_id: c.product_id, quantity: Math.max(1, +c.quantity || 1) })),
      pricing_mode: form.pricing_mode,
      fixed_price: parseFloat(form.fixed_price) || 0,
      percent_off: parseFloat(form.percent_off) || 0,
      is_active: !!form.is_active,
    };
    try {
      if (editing) {
        await api.put(`/admin/bundles/${editing.id}`, payload);
        toast.success("Bundle updated");
      } else {
        await api.post("/admin/bundles", payload);
        toast.success("Bundle created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save bundle");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete the bundle "${b.name}"?`)) return;
    try {
      await api.delete(`/admin/bundles/${b.id}`);
      toast.success("Bundle deleted");
      load();
    } catch {
      toast.error("Failed to delete bundle");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#6E675F]" data-testid="admin-bundles-count">
          <strong className="text-[#1A1A1A]">{bundles.length}</strong> bundle{bundles.length === 1 ? "" : "s"} — active ones appear on the public Bundles page.
        </p>
        <Button onClick={openCreate} data-testid="add-bundle-button" className="bg-[#8C7B6B] hover:bg-[#75665A]">
          <Plus className="w-4 h-4 mr-1" /> New Bundle
        </Button>
      </div>

      {bundles.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#DDD7CF] rounded-lg" data-testid="admin-bundles-empty">
          <Boxes className="w-10 h-10 text-[#8C7B6B] mx-auto mb-4" />
          <p className="text-[#6E675F]">No bundles yet — create your first curated stack.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="admin-bundles-list">
          {bundles.map((b) => (
            <div key={b.id} className="border border-[#DDD7CF] rounded-lg bg-white p-5" data-testid={`admin-bundle-row-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium text-[#1A1A1A]">{b.name}</h3>
                    <span className={`text-[10px] tracking-[0.12em] uppercase font-bold px-2 py-0.5 rounded-none border ${b.is_active ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-[#EFEBE6] text-[#6E675F] border-[#DDD7CF]"}`} data-testid={`admin-bundle-status-${b.id}`}>
                      {b.is_active ? "Active" : "Hidden"}
                    </span>
                    {!b.available && b.is_active && (
                      <span className="text-[10px] tracking-[0.12em] uppercase font-bold px-2 py-0.5 rounded-none bg-amber-50 text-amber-800 border border-amber-300">Out of stock</span>
                    )}
                  </div>
                  <p className="text-sm text-[#6E675F] mt-1.5">
                    {b.components_detail.map((c) => `${c.quantity > 1 ? `${c.quantity}× ` : ""}${c.name} ${c.dosage}`).join(" + ")}
                  </p>
                  <p className="text-sm mt-2">
                    <strong className="text-[#1A1A1A]">${b.bundle_price.toFixed(2)}</strong>
                    {b.savings > 0 && <span className="text-[#6E675F]"> (was ${b.components_total.toFixed(2)} — customer saves ${b.savings.toFixed(2)})</span>}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openEdit(b)} data-testid={`admin-bundle-edit-${b.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(b)} data-testid={`admin-bundle-delete-${b.id}`} className="text-red-700 border-red-200 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="bundle-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">{editing ? "Edit Bundle" : "New Bundle"}</DialogTitle>
            <DialogDescription>Curate one or more products into a stack with combined pricing — bulk units of a single product work too.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="b-name">Bundle Name</Label>
              <Input id="b-name" data-testid="bundle-form-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1" placeholder="e.g. Recovery Stack" />
            </div>
            <div>
              <Label htmlFor="b-desc">Description</Label>
              <Textarea id="b-desc" data-testid="bundle-form-description" rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>Bundle Image (optional — first product's photo is used if empty)</Label>
              <div className="mt-1">
                <ImageUploader value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))} testid="bundle-form-image-upload" label="Bundle image" />
              </div>
            </div>

            <div className="border-t border-[#DDD7CF] pt-4">
              <Label>Products in this bundle</Label>
              <div className="space-y-2 mt-2" data-testid="bundle-components-editor">
                {form.components.map((c, i) => (
                  <div key={`comp-${i}`} className="flex gap-2 items-center">
                    <select
                      data-testid={`bundle-comp-product-${i}`}
                      value={c.product_id}
                      onChange={(e) => setComp(i, { product_id: e.target.value })}
                      className="flex-1 min-w-0 border border-[#DDD7CF] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#8C7B6B]"
                    >
                      <option value="">Select product…</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>{p.name} {p.dosage} — ${p.price.toFixed(2)}</option>
                      ))}
                    </select>
                    <Input type="number" min="1" data-testid={`bundle-comp-qty-${i}`} value={c.quantity} onChange={(e) => setComp(i, { quantity: e.target.value })} className="w-20" />
                    <Button size="icon" variant="outline" onClick={() => removeComp(i)} data-testid={`bundle-comp-remove-${i}`} className="text-red-700 border-red-200 hover:bg-red-50 shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" onClick={addComp} data-testid="bundle-add-component-button">
                  <Plus className="w-4 h-4 mr-1" /> Add Product
                </Button>
              </div>
            </div>

            <div className="border-t border-[#DDD7CF] pt-4">
              <Label>Pricing</Label>
              <div className="flex gap-3 mt-2">
                {["percent", "fixed"].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    data-testid={`bundle-pricing-${mode}`}
                    onClick={() => setForm((f) => ({ ...f, pricing_mode: mode }))}
                    className={`flex-1 text-sm border-2 rounded-lg px-3 py-2.5 transition-colors ${form.pricing_mode === mode ? "border-[#8C7B6B] bg-[#EFEBE6]/60 font-semibold" : "border-[#DDD7CF]"}`}
                  >
                    {mode === "percent" ? "% off the total" : "Fixed price"}
                  </button>
                ))}
              </div>
              <div className="mt-3">
                {form.pricing_mode === "percent" ? (
                  <div>
                    <Label htmlFor="b-pct">Percent Off (%)</Label>
                    <Input id="b-pct" type="number" min="1" max="99" step="0.5" data-testid="bundle-form-percent-off" value={form.percent_off} onChange={(e) => setForm((f) => ({ ...f, percent_off: e.target.value }))} className="mt-1" />
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="b-fixed">Bundle Price (CAD)</Label>
                    <Input id="b-fixed" type="number" min="0" step="0.01" data-testid="bundle-form-fixed-price" value={form.fixed_price} onChange={(e) => setForm((f) => ({ ...f, fixed_price: e.target.value }))} className="mt-1" />
                  </div>
                )}
              </div>
              <p className="text-xs text-[#6E675F] mt-3" data-testid="bundle-price-preview">
                Components total: <strong className="text-[#1A1A1A]">${componentsTotal.toFixed(2)}</strong>
                {" · "}Bundle price: <strong className="text-[#1A1A1A]">${Math.max(0, previewPrice).toFixed(2)}</strong>
                {componentsTotal > previewPrice && previewPrice > 0 && (
                  <span className="text-[#2D6A4F]"> (saves ${(componentsTotal - previewPrice).toFixed(2)})</span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-3 border-t border-[#DDD7CF] pt-4">
              <Switch checked={!!form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="bundle-form-active" />
              <Label>Visible on the Bundles page</Label>
            </div>

            <Button onClick={save} disabled={saving} data-testid="bundle-form-save" className="w-full bg-[#8C7B6B] hover:bg-[#75665A]">
              {saving ? "Saving…" : editing ? "Save Changes" : "Create Bundle"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
